import { chacha20poly1305 } from "@noble/ciphers/chacha.js";
import { blake3 } from "@noble/hashes/blake3.js";
import { bytesToHex } from "@noble/hashes/utils.js";

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const MAX_EVENT_BYTES = 20 * 1024 * 1024;
const MAX_IMAGE_BYTES = 12 * 1024 * 1024;
const MAX_HISTORY_BYTES = 64 * 1024 * 1024;
const MAX_HISTORY_PIXELS = 120_000_000;
const MAX_IMAGE_PIXELS = 40_000_000;
const MAX_CLIPS = 500;

export function decodeBase64Url(value, maximumCharacters = 700_000) {
  if (typeof value !== "string" || value.length < 1 || value.length > maximumCharacters
    || !/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("Encoded History data is not valid.");
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(value.replaceAll("-", "+").replaceAll("_", "/") + padding);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function parseRecoveryKey(value) {
  const encoded = String(value ?? "").trim().replace(/^CSP1-/, "");
  const key = decodeBase64Url(encoded, 64);
  if (key.length !== 32) throw new Error("That recovery key is not valid.");
  return key;
}

function eventHeader(event) {
  return {
    protocol_version: 1,
    event_id: event.id,
    device_id: event.deviceId,
    sequence: Number(event.sequence),
    created_unix_ms: Number(event.createdAt),
    content_hash: event.contentHash,
  };
}

export function decryptSyncEvent(event, key) {
  if (!event || !Array.isArray(event.chunks) || event.chunks.length < 1 || event.chunks.length > 96) {
    throw new Error("An encrypted History item is not valid.");
  }
  const decodedChunks = event.chunks.map((chunk) => decodeBase64Url(chunk));
  const ciphertextBytes = decodedChunks.reduce((total, chunk) => total + chunk.length, 0);
  if (!Number.isSafeInteger(ciphertextBytes) || ciphertextBytes < 17 || ciphertextBytes > MAX_EVENT_BYTES) {
    throw new Error("An encrypted History item exceeds the browser safety limit.");
  }
  const ciphertext = new Uint8Array(ciphertextBytes);
  let offset = 0;
  for (const chunk of decodedChunks) {
    ciphertext.set(chunk, offset);
    offset += chunk.length;
  }
  const plaintext = chacha20poly1305(
    key,
    decodeBase64Url(event.nonce, 64),
    encoder.encode(JSON.stringify(eventHeader(event))),
  ).decrypt(ciphertext);
  if (bytesToHex(blake3(plaintext)) !== event.contentHash) throw new Error("An encrypted History item failed its integrity check.");
  if (plaintext.byteLength > MAX_EVENT_BYTES) throw new Error("An encrypted History item exceeds the browser safety limit.");
  return JSON.parse(decoder.decode(plaintext));
}

function imageEnvelope(encoded) {
  if (typeof encoded !== "string" || encoded.length > Math.ceil(MAX_IMAGE_BYTES / 3) * 4 + 4) {
    throw new Error("A synced image exceeds the browser safety limit.");
  }
  const bytes = decodeBase64Url(encoded, Math.ceil(MAX_IMAGE_BYTES / 3) * 4 + 4);
  if (!bytes.length || bytes.byteLength > MAX_IMAGE_BYTES) throw new Error("A synced image exceeds the browser safety limit.");
  let mime;
  let width;
  let height;
  if (bytes.length >= 24 && [137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => bytes[index] === byte)) {
    if (![73, 72, 68, 82].every((byte, index) => bytes[index + 12] === byte)) throw new Error("A synced image is not valid.");
    for (let index = 8; index + 12 <= bytes.length;) {
      const length = (bytes[index] << 24) | (bytes[index + 1] << 16) | (bytes[index + 2] << 8) | bytes[index + 3];
      if (length < 0 || index + 12 + length > bytes.length) throw new Error("A synced image is not valid.");
      const type = String.fromCharCode(...bytes.slice(index + 4, index + 8));
      if (type === "acTL") throw new Error("Animated images are not available in Web History.");
      index += 12 + length;
    }
    width = new DataView(bytes.buffer, bytes.byteOffset + 16, 8).getUint32(0);
    height = new DataView(bytes.buffer, bytes.byteOffset + 16, 8).getUint32(4);
    mime = "image/png";
  } else if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes.at(-2) === 0xff && bytes.at(-1) === 0xd9) {
    let offset = 2;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xff) { offset += 1; continue; }
      const marker = bytes[offset + 1];
      if (marker === 0xd8 || marker === 0xd9) { offset += 2; continue; }
      const length = (bytes[offset + 2] << 8) | bytes[offset + 3];
      if (length < 2 || offset + 2 + length > bytes.length) throw new Error("A synced image is not valid.");
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker)) {
        height = (bytes[offset + 5] << 8) | bytes[offset + 6];
        width = (bytes[offset + 7] << 8) | bytes[offset + 8];
        break;
      }
      offset += 2 + length;
    }
    mime = "image/jpeg";
  } else if (bytes.length >= 20 && [82, 73, 70, 70].every((byte, index) => bytes[index] === byte)
    && [87, 69, 66, 80].every((byte, index) => bytes[index + 8] === byte)) {
    if (new DataView(bytes.buffer, bytes.byteOffset + 4, 4).getUint32(0, true) + 8 !== bytes.length) {
      throw new Error("A synced image is not valid.");
    }
    for (let offset = 12; offset + 8 <= bytes.length;) {
      const chunk = String.fromCharCode(...bytes.slice(offset, offset + 4));
      const length = new DataView(bytes.buffer, bytes.byteOffset + offset + 4, 4).getUint32(0, true);
      const data = offset + 8;
      if (!Number.isSafeInteger(length) || data + length > bytes.length) throw new Error("A synced image is not valid.");
      if (chunk === "ANIM" || chunk === "ANMF" || chunk === "VP8X" && (bytes[data] & 0x02) !== 0) {
        throw new Error("Animated images are not available in Web History.");
      }
      if (chunk === "VP8X" && length >= 10) {
        width = 1 + bytes[data + 4] + (bytes[data + 5] << 8) + (bytes[data + 6] << 16);
        height = 1 + bytes[data + 7] + (bytes[data + 8] << 8) + (bytes[data + 9] << 16);
      } else if (chunk === "VP8 " && length >= 10 && bytes[data + 3] === 0x9d && bytes[data + 4] === 0x01 && bytes[data + 5] === 0x2a) {
        width = (bytes[data + 6] | bytes[data + 7] << 8) & 0x3fff;
        height = (bytes[data + 8] | bytes[data + 9] << 8) & 0x3fff;
      } else if (chunk === "VP8L" && length >= 5 && bytes[data] === 0x2f) {
        width = 1 + bytes[data + 1] + ((bytes[data + 2] & 0x3f) << 8);
        height = 1 + (bytes[data + 2] >> 6) + (bytes[data + 3] << 2) + ((bytes[data + 4] & 0x0f) << 10);
      }
      offset = data + length + (length % 2);
    }
    if (!width || !height) throw new Error("A synced image is not valid.");
    mime = "image/webp";
  } else {
    throw new Error("A synced image type is not supported.");
  }
  const pixels = Number(width) * Number(height);
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1
    || !Number.isSafeInteger(pixels) || pixels > MAX_IMAGE_PIXELS) throw new Error("A synced image exceeds the browser pixel limit.");
  return { bytes, mime, width, height, pixels };
}

// Only object URLs minted by applySyncEvent may reach markup or fetch.
export function safeWebImageUrl(value) {
  return typeof value === "string" && value.startsWith("blob:") ? value : "";
}

export function createWebHistoryAccumulator() {
  return { clips: new Map(), objectUrls: [], materializedBytes: 0, materializedPixels: 0 };
}

export function disposeWebHistoryAccumulator(accumulator) {
  for (const url of accumulator?.objectUrls ?? []) URL.revokeObjectURL(url);
  if (accumulator?.objectUrls) accumulator.objectUrls.length = 0;
}

export function applySyncEvent(accumulator, event, key) {
  const payload = decryptSyncEvent(event, key);
  if (!payload || Object.getPrototypeOf(payload) !== Object.prototype || typeof payload.kind !== "string") {
    throw new Error("A synced History item is not valid.");
  }
  if (payload.kind === "tombstone") {
    if (Reflect.ownKeys(payload).length !== 2 || typeof payload.clip_id !== "string") {
      throw new Error("A synced History item is not valid.");
    }
    accumulator.clips.delete(payload.clip_id);
    return;
  }
  if (payload.kind !== "clipboard") throw new Error("A synced History item is not valid.");
  const payloadKeys = Reflect.ownKeys(payload);
  if (payloadKeys.length !== 5 || payloadKeys.some((key) => !["kind", "clip_id", "media_type", "bytes", "sensitive"].includes(key))) {
    throw new Error("A synced History item is not valid.");
  }
  if (typeof payload.sensitive !== "boolean" || typeof payload.media_type !== "string") {
    throw new Error("A synced History item is not valid.");
  }
  if (payload.sensitive) return;
  if (typeof payload.clip_id !== "string" || !Array.isArray(payload.bytes) || payload.bytes.length > MAX_EVENT_BYTES
    || payload.bytes.some((byte) => !Number.isInteger(byte) || byte < 0 || byte > 255)) throw new Error("A synced History item is not valid.");
  const wireBytes = Uint8Array.from(payload.bytes);
  const wire = JSON.parse(decoder.decode(wireBytes));
  if (!wire || Object.getPrototypeOf(wire) !== Object.prototype || Reflect.ownKeys(wire).length !== 2
    || !Object.hasOwn(wire, "clip") || !Object.hasOwn(wire, "imageData") || !wire.clip
    || Object.getPrototypeOf(wire.clip) !== Object.prototype || !(wire.imageData === null || typeof wire.imageData === "string")) {
    throw new Error("A synced History item is not valid.");
  }
  if (wire.clip.sensitive === true || (typeof wire.clip.sensitiveType === "string" && wire.clip.sensitiveType)) {
    return;
  }
  accumulator.materializedBytes += wireBytes.byteLength;
  if (!Number.isSafeInteger(accumulator.materializedBytes) || accumulator.materializedBytes > MAX_HISTORY_BYTES) {
    throw new Error("Web History reached its browser memory safety limit.");
  }
  const clip = {
    ...wire.clip,
    id: payload.clip_id,
    originDeviceId: wire.clip.originDeviceId || event.deviceId,
    originDeviceName: wire.clip.originDeviceName || event.deviceName || "Connected computer",
    syncedAt: Number(event.createdAt),
  };
  // webImageUrl is local browser state; a peer must not supply it.
  delete clip.webImageUrl;
  if (wire.imageData) {
    if (clip.kind !== "image") throw new Error("A synced image does not match its declared clip type.");
    const image = imageEnvelope(wire.imageData);
    const declared = clip.mimeType ?? clip.mediaType ?? clip.imageMimeType;
    if (declared && declared !== image.mime) throw new Error("A synced image does not match its declared type.");
    accumulator.materializedBytes += image.bytes.byteLength;
    accumulator.materializedPixels += image.pixels;
    if (accumulator.materializedBytes > MAX_HISTORY_BYTES || accumulator.materializedPixels > MAX_HISTORY_PIXELS) {
      throw new Error("Web History reached its browser memory safety limit.");
    }
    clip.mimeType = image.mime;
    clip.width = image.width;
    clip.height = image.height;
    clip.webImageUrl = URL.createObjectURL(new Blob([image.bytes], { type: image.mime }));
    accumulator.objectUrls.push(clip.webImageUrl);
  }
  const previous = accumulator.clips.get(payload.clip_id);
  clip.copyOrigins = mergeOrigins([
    ...originsForClip(previous),
    originForClip(clip),
  ]);
  accumulator.clips.set(payload.clip_id, clip);
  if (accumulator.clips.size > MAX_CLIPS || accumulator.objectUrls.length > MAX_CLIPS) {
    throw new Error("Web History reached its item safety limit.");
  }
}

function clipTime(clip) {
  return Number(clip.lastUsedAt ?? clip.createdAt ?? clip.syncedAt ?? 0);
}

function clipIdentity(clip) {
  if (clip.kind === "image") return clip.imageHash ? `image:${clip.imageHash}` : `image-id:${clip.id}`;
  return `text:${String(clip.text ?? "")}`;
}

function originForClip(clip) {
  return {
    id: clip.originDeviceId || "unknown",
    name: clip.originDeviceName || "Connected computer",
    copiedAt: clipTime(clip),
  };
}

function originsForClip(clip) {
  if (!clip) return [];
  return Array.isArray(clip.copyOrigins) && clip.copyOrigins.length
    ? clip.copyOrigins
    : [originForClip(clip)];
}

function mergeOrigins(origins) {
  const merged = new Map();
  for (const origin of origins) {
    const id = origin.id || "unknown";
    const existing = merged.get(id);
    if (!existing || Number(origin.copiedAt) > Number(existing.copiedAt)) merged.set(id, { ...origin, id });
  }
  return [...merged.values()].sort((a, b) => Number(b.copiedAt) - Number(a.copiedAt));
}

export function collapseHistoryClips(clips) {
  const collapsed = new Map();
  const sorted = [...clips].sort((a, b) => clipTime(b) - clipTime(a));
  for (const clip of sorted) {
    const identity = clipIdentity(clip);
    const existing = collapsed.get(identity);
    if (!existing) {
      collapsed.set(identity, {
        ...clip,
        count: Math.max(1, Number(clip.count) || 1),
        copyOrigins: originsForClip(clip),
      });
      continue;
    }
    existing.count += Math.max(1, Number(clip.count) || 1);
    existing.copyOrigins = mergeOrigins([...existing.copyOrigins, ...originsForClip(clip)]);
  }
  return [...collapsed.values()];
}

export function finishWebHistory(accumulator) {
  return {
    clips: collapseHistoryClips([...accumulator.clips.values()]),
    objectUrls: accumulator.objectUrls,
  };
}

export function materializeWebHistory(events, key) {
  const accumulator = createWebHistoryAccumulator();
  for (const event of events) applySyncEvent(accumulator, event, key);
  return finishWebHistory(accumulator);
}
